"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { getUserPlaceholderImage } from "../../../utils/imageUtils";
import { likesAPI } from "../../../services/api";

const PostReactionsModal = ({ isOpen, onClose, postId, currentUser }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getUserType } = useAuth();
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const scrollLockRef = useRef(0);

  // Custom emoji image paths
  const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
  const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
  const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
  const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
  const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
  const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";

  // Reaction type to emoji mapping
  const reactionTypeToEmoji = {
    like: HappyEmoji,
    love: LoveEmoji,
    haha: LaughEmoji,
    funny: LaughEmoji,
    laugh: LaughEmoji,
    wow: ShockedEmoji,
    sad: SadEmoji,
    angry: AngryEmoji,
  };

  // Reaction type to locale label key (postFeed.reactions.*)
  const reactionTypeToLabelKey = {
    like: "like",
    love: "love",
    haha: "haha",
    funny: "haha",
    laugh: "haha",
    wow: "wow",
    sad: "sad",
    angry: "angry",
  };

  // Get user type color
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      PET_GUARDIAN: "#8548A8", // Purple
      VETERINARIAN: "#00D4B3", // Cyan
      SERVICE_PROVIDER: "#D65745", // Orange
      SERVICE_PROVIDER: "#D65745", // Orange
      CLINIC_MODERATOR: "#2A8AD2", // Blue
    };
    return userTypeConfig[userType] || "#8548A8"; // Default to purple
  };

  // Group reactions by type
  const groupedReactions = useMemo(() => {
    if (!reactions || reactions.length === 0) return {};

    const grouped = {};
    reactions.forEach((reaction) => {
      const type = reaction.type || reaction.reaction_type;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(reaction);
    });

    // Sort each group by created_at (most recent first)
    Object.keys(grouped).forEach((type) => {
      grouped[type].sort((a, b) => {
        const dateA = new Date(a.created_at || a.createdAt || 0);
        const dateB = new Date(b.created_at || b.createdAt || 0);
        return dateB - dateA;
      });
    });

    return grouped;
  }, [reactions]);

  // Get all reaction types in order
  const reactionTypes = useMemo(() => {
    const types = ["haha", "love", "wow", "sad", "angry", "like"];
    return types.filter((type) => groupedReactions[type]?.length > 0);
  }, [groupedReactions]);

  // Set default selected tab to first available reaction type
  useEffect(() => {
    if (reactionTypes.length > 0 && !selectedTab) {
      setSelectedTab(reactionTypes[0]);
    } else if (reactionTypes.length > 0 && !reactionTypes.includes(selectedTab)) {
      setSelectedTab(reactionTypes[0]);
    }
  }, [reactionTypes, selectedTab]);

  // Fetch reactions when modal opens
  useEffect(() => {
    if (isOpen && postId) {
      fetchReactions();
    } else {
      // Reset state when modal closes
      setReactions([]);
      setError(null);
      setSelectedTab(null);
    }
  }, [isOpen, postId]);

  const fetchReactions = async () => {
    if (!postId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await likesAPI.getPostReactions(postId);

      // Handle different response structures
      let reactionsData = [];

      // Handle paginated response (Laravel pagination) - check this first
      if (response?.data?.data && Array.isArray(response.data.data)) {
        reactionsData = response.data.data;
      }
      // Handle direct reactions array
      else if (response?.data?.reactions && Array.isArray(response.data.reactions)) {
        reactionsData = response.data.reactions;
      }
      // Handle direct array response
      else if (Array.isArray(response?.data)) {
        reactionsData = response.data;
      }

      // Transform reactions to ensure consistent structure
      const transformedReactions = reactionsData.map((reaction) => {
        // Normalize reaction type (handle "funny", "haha", "laugh" as same)
        let reactionType = reaction.type || reaction.reaction_type;
        if (reactionType === "funny" || reactionType === "laugh") {
          reactionType = "haha";
        }

        return {
          id: reaction.id,
          type: reactionType,
          user: reaction.user || reaction.user_data || {},
          created_at: reaction.created_at || reaction.createdAt,
        };
      });

      setReactions(transformedReactions);
    } catch (err) {
      console.error("Error fetching reactions:", err);
      setError(t("postReactionsModal.error", "Failed to load reactions"));
    } finally {
      setLoading(false);
    }
  };

  // Handle Escape key and body scroll lock
  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Handle user profile navigation
  const handleUserClick = (user) => {
    if (!user) return;

    const userId = user.id || user.user_id;
    const username = user.username;

    if (currentUser && (currentUser.id === userId || currentUser.user_id === userId)) {
      navigate("/social/profile");
    } else if (userId) {
      navigate(`/social/profile/${userId}`);
    } else if (username) {
      navigate(`/social/profile/${username}`);
    }

    onClose();
  };

  if (!isOpen) return null;

  const totalReactions = reactions.length;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        backdropFilter: "blur(4px)",
        padding: "20px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: "600",
              color: "#111827",
              fontFamily: '"Red Hat Display", sans-serif',
            }}
          >
            {t("postReactionsModal.title", "Reactions")}
          </h2>
          <button
            onClick={onClose}
            aria-label={t("common.buttons.close", "Close")}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#6b7280",
              padding: "8px",
              borderRadius: "8px",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#f3f4f6";
              e.target.style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#6b7280";
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24x 0 0 0 24px",
            minHeight: "300px",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px",
                color: "#6b7280",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid #e5e7eb",
                  borderTop: "3px solid #8548A8",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginBottom: "16px",
                }}
              />
              <p style={{ margin: 0, fontSize: "16px" }}>
                {t("postReactionsModal.loading", "Loading reactions...")}
              </p>
            </div>
          ) : error ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px",
                color: "#ef4444",
              }}
            >
              <p style={{ margin: 0, fontSize: "16px" }}>{error}</p>
            </div>
          ) : totalReactions === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px",
                color: "#6b7280",
              }}
            >
              <p style={{ margin: 0, fontSize: "16px" }}>
                {t("postReactionsModal.noReactions", "No reactions yet")}
              </p>
            </div>
          ) : (
            <div>
              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "0px",
                  borderBottom: "2px solid #e5e7eb",
                  scrollbarWidth: "thin",
                }}
              >
                {reactionTypes.map((type) => {
                  const typeReactions = groupedReactions[type] || [];
                  const emoji = reactionTypeToEmoji[type] || HappyEmoji;
                  const isActive = selectedTab === type;

                  return (
                    <button
                      key={type}
                      onClick={() => setSelectedTab(type)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "12px 16px",
                        background: "none",
                        border: "none",
                        borderBottom: isActive ? "3px solid #8548A8" : "3px solid transparent",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        fontFamily: '"Red Hat Display", sans-serif',
                        fontSize: "15px",
                        fontWeight: isActive ? "600" : "500",
                        color: isActive ? "#8548A8" : "#6b7280",
                        whiteSpace: "nowrap",
                        marginBottom: "-2px",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = "#111827";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = "#6b7280";
                        }
                      }}
                    >
                      {(() => {
                        const isImagePath = typeof emoji === 'string' && (emoji.startsWith('/') || emoji.includes('.png') || emoji.includes('.jpg') || emoji.includes('.svg'));
                        return isImagePath ? (
                          <img
                            src={emoji}
                            alt={type}
                            style={{ width: "40px", height: "40px", objectFit: 'contain', display: 'inline-block' }}
                          />
                        ) : (
                          <span style={{ fontSize: "28px" }}>{emoji}</span>
                        );
                      })()}
                      <span>{t(`postFeed.reactions.${reactionTypeToLabelKey[type] || "like"}`, type)}</span>
                    </button>
                  );
                })}
              </div>

              {/* Selected Tab Content */}
              {selectedTab && groupedReactions[selectedTab] && (
                <div>
                  {/* User List */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {groupedReactions[selectedTab].map((reaction) => {
                      const user = reaction.user || {};
                      const userId = user.id || user.user_id;
                      const firstName = user.first_name || "";
                      const lastName = user.last_name || "";
                      const username = user.username || "";
                      const profileImage =
                        user.profile_image_url ||
                        user.profileImage ||
                        user.avatar ||
                        getUserPlaceholderImage(user);
                      const userType = user.user_type || user.userType;
                      const userTypeColor = getUserTypeColor(userType);

                      const displayName =
                        firstName && lastName
                          ? `${firstName} ${lastName}`
                          : firstName || lastName || username || "Unknown User";

                      return (
                        <div
                          key={reaction.id || `${userId}-${selectedTab}`}
                          onClick={() => handleUserClick(user)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f9fafb";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          {/* Avatar */}
                          <img
                            src={profileImage}
                            alt={displayName}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: `3px solid ${userTypeColor}`,
                              boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.8)",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              e.target.src = getUserPlaceholderImage(user);
                            }}
                          />

                          {/* User Info */}
                          <div
                            style={{
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: "#111827",
                                fontFamily: '"Red Hat Display", sans-serif',
                                marginBottom: "4px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {displayName}
                            </div>
                            {/* {username && (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#6b7280",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                @{username}
                              </div>
                            )} */}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default PostReactionsModal;

